import { ReactNode } from "react";
import { useT } from "../lang/LangContext";
import { SectionHeading } from "./SectionHeading";

interface Job {
  titleEn: ReactNode;
  titlePt: ReactNode;
  company: string;
  dateEn: ReactNode;
  datePt: ReactNode;
  current?: boolean;
  bullets: [string, string][];
}

const JOBS: Job[] = [
  {
    titleEn: "Senior Full-Stack Engineer",
    titlePt: "Senior Full-Stack Engineer",
    company: "Quadrant Strategies",
    dateEn: "PRESENT",
    datePt: "ATUAL",
    current: true,
    bullets: [
      [
        "Build and own production AI-powered internal platforms with Python, LangGraph, LLMs and Next.js to automate complex business workflows.",
        "Construo e sou responsável por plataformas internas de produção com IA, usando Python, LangGraph, LLMs e Next.js para automatizar fluxos de trabalho complexos.",
      ],
      [
        "Lead the full product lifecycle: architecture, implementation, deployment, monitoring, maintenance and continuous improvement.",
        "Lidero o ciclo completo do produto: arquitetura, implementação, deploy, monitoramento, manutenção e melhoria contínua.",
      ],
      [
        "Own cloud infrastructure, environment management, production reliability and CI/CD pipelines for faster, repeatable releases.",
        "Sou responsável pela infraestrutura em nuvem, gestão de ambientes, confiabilidade em produção e pipelines de CI/CD para releases mais rápidos e repetíveis.",
      ],
      [
        "Deliver tools that cut manual work and lower operational overhead across the company.",
        "Entrego ferramentas que reduzem trabalho manual e diminuem o custo operacional em toda a empresa.",
      ],
    ],
  },
  {
    titleEn: "Senior Full-Stack Engineer",
    titlePt: "Senior Full-Stack Engineer",
    company: "Bemprotege",
    dateEn: "JAN 2025 → OCT 2025",
    datePt: "JAN 2025 → OUT 2025",
    bullets: [
      [
        "Developed full-stack insurance platforms with React, Next.js, Tailwind CSS, Python and Java, backed by optimized SQL queries and data models.",
        "Desenvolvi plataformas de seguros full-stack com React, Next.js, Tailwind CSS, Python e Java, apoiadas em consultas SQL e modelos de dados otimizados.",
      ],
      [
        "Designed scalable backend services and APIs while keeping automated test coverage high.",
        "Projetei serviços de backend e APIs escaláveis, mantendo alta cobertura de testes automatizados.",
      ],
      [
        "Led AI agent and LLM integrations that automated internal workflows, and implemented Stripe payments for SaaS products.",
        "Liderei integrações de agentes de IA e LLMs que automatizaram fluxos internos, e implementei pagamentos com Stripe para produtos SaaS.",
      ],
    ],
  },
  {
    titleEn: "Blockchain Engineer",
    titlePt: "Blockchain Engineer",
    company: "SNACKCLUB",
    dateEn: "JAN 2022 → JAN 2023",
    datePt: "JAN 2022 → JAN 2023",
    bullets: [
      [
        "Built blockchain applications and smart contracts with automated test suites in Mocha and Jest.",
        "Construí aplicações blockchain e contratos inteligentes com suítes de testes automatizados em Mocha e Jest.",
      ],
      [
        "Shipped full-stack features with React and Next.js, managed SQL/NoSQL data, and deployed containerized workloads to Kubernetes on DigitalOcean.",
        "Entreguei funcionalidades full-stack com React e Next.js, gerenciei dados SQL/NoSQL e fiz deploy de cargas conteinerizadas em Kubernetes na DigitalOcean.",
      ],
    ],
  },
  {
    titleEn: "Backend Developer",
    titlePt: "Backend Developer",
    company: "LOUD",
    dateEn: "JUL 2021 → JAN 2022",
    datePt: "JUL 2021 → JAN 2022",
    bullets: [
      [
        "Built backend services with Java, Node.js and Express; tuned database queries and expanded automated testing for production reliability.",
        "Construí serviços de backend com Java, Node.js e Express; otimizei consultas ao banco e ampliei os testes automatizados para dar confiabilidade à produção.",
      ],
    ],
  },
  {
    titleEn: "Web Development Intern",
    titlePt: "Estagiário de Desenvolvimento Web",
    company: "Exacode",
    dateEn: "NOV 2020 → SEP 2021",
    datePt: "NOV 2020 → SET 2021",
    bullets: [
      [
        "Supported JavaScript and Java web applications, wrote SQL scripts, and automated quality checks with Jest and Selenium.",
        "Dei suporte a aplicações web em JavaScript e Java, escrevi scripts SQL e automatizei verificações de qualidade com Jest e Selenium.",
      ],
    ],
  },
];

export function Experience() {
  const t = useT();

  return (
    <section
      id="work"
      style={{ maxWidth: 1180, margin: "0 auto", padding: "36px 20px 64px" }}
    >
      <SectionHeading index="03">
        {t("Experience", "Experiência")}
      </SectionHeading>
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {JOBS.map((job) => (
          <article
            key={job.company}
            style={{
              border: "3px solid var(--fg)",
              background: "var(--card)",
              padding: 24,
              boxShadow: `8px 8px 0 ${
                job.current ? "var(--accent)" : "var(--fg)"
              }`,
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "baseline",
                justifyContent: "space-between",
                marginBottom: 14,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontFamily: "'Archivo Black',sans-serif",
                  fontSize: "clamp(20px,2.6vw,28px)",
                  letterSpacing: "-0.02em",
                  textTransform: "uppercase",
                }}
              >
                {t(job.titleEn, job.titlePt)}{" "}
                <span style={{ color: "var(--accent)" }}>@ {job.company}</span>
              </h3>
              <span
                style={{
                  fontSize: 12,
                  letterSpacing: "0.12em",
                  border: `2px solid ${
                    job.current ? "var(--accent)" : "var(--fg)"
                  }`,
                  color: job.current ? "var(--accent)" : "var(--fg)",
                  padding: "4px 9px",
                }}
              >
                {job.current ? (
                  <>OCT 2025 → {t(job.dateEn, job.datePt)}</>
                ) : (
                  t(job.dateEn, job.datePt)
                )}
              </span>
            </div>
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                display: "flex",
                flexDirection: "column",
                gap: 10,
                fontSize: 15,
                lineHeight: 1.6,
              }}
            >
              {job.bullets.map(([en, pt]) => (
                <li key={en} style={{ display: "flex", gap: 12 }}>
                  <span style={{ color: "var(--accent)", fontWeight: 700 }}>
                    ▚
                  </span>
                  <span>{t(en, pt)}</span>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </section>
  );
}
